import React, { useEffect, useState } from 'react';
import Stylesheet from './Table.module.css';
import Thead from './components/Thead/Thead';
import Tbody from './components/Tbody/Tbody';
import Loading from '../Spinner/Standard/Standard';
import Toolbox from './components/Toolbox/Toolbox';
import { useLocation } from 'react-router-dom';
import { QueryContext } from './context/query';
import { TableContext } from './context/table';
import Visual from './components/Visuals/Visual';
import Metadata from './components/Metadata/Metadata';
import {
    getMinRange, getSearch,
    getSelectedColumns, getSelectedRecords,
    getRecordRange, getSearchRecords, sync
} from './Table.Engine';
import util from '@enigma/core/utilities';

const Table = (props) => {
    const location = useLocation();
    const document = props?.document;
    const collection = props?.collection || `${document}/records`;
    const metadata = { ...props?.metadata, collection: collection, document: document};
    const [syncing, setSyncing] = useState(true);
    const [schema, setSchema] = useState(props?.schema || props.metadata.schema || null);
    const [records, setRecords] = useState([]);
    const [config, setConfig] = useState({
        custom: props?.custom || false,
        addText: props?.addText,
        deleteText: props?.deleteText,
        toolbox: props?.toolbox,
        disableFilter: props?.disableFilter,
        basic: props?.basic,
        lock: props?.lock || false,
        disableID: props?.disableID || false,
        disableAuthor: props?.disableAuthor || false,
        disableTimestamp: props?.disableTimestamp || false,
        disableImport: props?.disableImport,
        disableAdd: props?.disableAdd,
        disableDelete: props?.disableDelete,
        disableExport: props?.disableExport,
        disableData: props?.disableData,
        disableReports: props?.disableReports,
        visualizationMode: false,
        recordCriteria: props?.recordCriteria,
        recordLock: props?.recordLock,
        loadMoreRecordsLock: false
    })
    const [minDateRange, setMinDateRange] = useState(getMinRange());
    const [maxDateRange, setMaxDateRange] = useState(Date.now());
    const [recordLimit, setRecordLimit] = useState(15);
    const [search, setSearch] = useState({ search: getSearch(location), records: [] })
    const [report, setReport] = useState({ report: null, records: [] })
    const [selected, setSelected] = useState({
        records: getSelectedRecords(location),
        columns: getSelectedColumns(location)
    })
    useEffect(() => {
        // reset query between each table change
        setSearch({ search: getSearch(location), records: [] });
        setSelected({ records: getSelectedRecords(location), columns: getSelectedColumns(location) });
        setRecordLimit(15);
        setConfig({ ...config, loadMoreRecordsLock: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props])
    // set search results after every search term update
    useEffect(() => {
        setSearch({
            ...search, records: getSearchRecords(
                location, selected, report, records, schema
            )
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search.search, records])
    // get records after table configuration
    useEffect(() => {
        // get record ranges
        const minRecordRange = getRecordRange(minDateRange, "min");
        const maxRecordRange = getRecordRange(maxDateRange, "max");
        // fetch table metadata once
        if (props?.custom && props?.document !== metadata.document) {
            // clear schema for new table
            setSchema(props?.schema || props.metadata.schema || null);
            // clear records for new table
            setRecords([]);
            // clear any reports
            setReport({ report: null, records: [] })
            // sync new table records
            setSyncing(true);
        }
        // sync records to device
        sync(config, { id: props.metadata.id, collection: collection },
            minRecordRange, maxRecordRange, (rangeHasRecords) => {
                // check if resulting records for current range is empty
                if (!rangeHasRecords) {
                    console.log("no records for current record range, going back 1 month");
                    // keep going back until the custom table was created
                    if (minRecordRange >= metadata.timestamp && config.custom) {
                        const minDate = new util.cdate(minRecordRange);
                        minDate.decrementMonth(1);
                        setMinDateRange(minDate.dateObject.getTime());
                    } else {
                        console.log("table does not have any records yet.");
                        setSyncing(false);
                    }
                } else setSyncing(false);
            })
        // attach lisnter 
        const database = new util.database("gmgroup.fleet");
        let lisnter = database.table(props.metadata.id)
            .where("timestamp", ">=", props?.custom ? minRecordRange : 0)
            .and("timestamp", "<=", maxRecordRange)
            .orderedBy("timestamp", "DESC")
            .onTransaction(async (transaction) => {
                const current = [];
                for (const document of transaction.collection)
                    current.push(document.data);
                setRecords(current);
            });
        return () => { lisnter() }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [document, minDateRange, maxDateRange])

    const content = _ => {
        if (schema === null || syncing) return (
            <React.Fragment>
                <Toolbox />
                <div className={Stylesheet.Loading}>
                    <Loading show />
                </div>
            </React.Fragment>
        )
        // render current grouped query in summarised visuals
        if (config.visualizationMode) return (
            <React.Fragment>
                <Toolbox />
                <div className={Stylesheet.Table}>
                    <Visual />
                </div>
            </React.Fragment>
        )
        // render default table view
        return (
            <React.Fragment>
                <Toolbox />
                <Metadata />
                <div className={Stylesheet.Table}>
                    <Thead />
                    <Tbody />
                </div>
            </React.Fragment>
        )
    }

    return (
        <QueryContext.Provider value={{
            search: search,
            report: report,
            selected: selected,
            setMinDate: date => setMinDateRange(date),
            setMaxDate: date => setMaxDateRange(date),
            setRecordLimit: limit => setRecordLimit(limit),
            setReport: metadata => setReport(metadata),
            setConfig: config => setConfig({ ...config })
        }}>
            <TableContext.Provider value={{
                metadata: metadata,
                records: records,
                schema: schema,
                config: config,
                filter: {
                    min_date: minDateRange,
                    max_date: maxDateRange,
                    recordLimit: recordLimit
                }
            }}>
                <div className={Stylesheet.Container}>
                    {content()}
                </div>
            </TableContext.Provider>
        </QueryContext.Provider>
    )
}

export default Table;